import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { SessionUser } from "@/features/auth/auth-provider";
import type { AccessGateState, PostLoginPath } from "@crewledger/shared/beta-access";
import type { WorkspaceSummary } from "@crewledger/shared/workspace";
import { POST_LOGIN_PATHS } from "@crewledger/shared/beta-access";
import { apiFetch } from "@/lib/api";

const E2E_ROLE_KEY = "werkio_e2e_role";

export const E2E_DEMO_ROLES = {
  owner: { name: "Sarah Mitchell", role: "OWNER", email: "sarah.mitchell@ridgepoint.demo" },
  admin: { name: "David Park", role: "ADMIN", email: "david.park@ridgepoint.demo" },
  gm: { name: "Elena Vasquez", role: "GM_PAYROLL", email: "elena.vasquez@ridgepoint.demo" },
  "site-manager-downtown": {
    name: "Marcus Webb",
    role: "SITE_MANAGER",
    email: "marcus.webb@ridgepoint.demo",
  },
  auditor: { name: "Robert Klein", role: "AUDITOR", email: "robert.klein@ridgepoint.demo" },
} as const;

/** Beta / access-gate personas — no pre-seeded workspace profile. */
export const E2E_BETA_ROLES = {
  "not-invited": { email: "e2e-not-invited@werkio.test" },
  "beta-invited": { email: "e2e-beta-invited@werkio.test" },
  "beta-blocked": { email: "e2e-beta-blocked@werkio.test" },
  "platform-admin": { email: "e2e-platform-admin@werkio.test" },
} as const;

export type E2eDemoRole = keyof typeof E2E_DEMO_ROLES;
export type E2eBetaRole = keyof typeof E2E_BETA_ROLES;
export type E2eRole = E2eDemoRole | E2eBetaRole;

function isE2eRole(value: string): value is E2eRole {
  return value in E2E_DEMO_ROLES || value in E2E_BETA_ROLES;
}

function readE2eRole(): E2eRole {
  if (import.meta.env.VITE_E2E_AUTH_BYPASS !== "true") return "owner";
  const fromUrl = new URLSearchParams(window.location.search).get("e2eRole");
  if (fromUrl && isE2eRole(fromUrl)) {
    localStorage.setItem(E2E_ROLE_KEY, fromUrl);
    return fromUrl;
  }
  const stored = localStorage.getItem(E2E_ROLE_KEY);
  return stored && isE2eRole(stored) ? stored : "owner";
}

interface E2eAuthContextValue {
  ready: boolean;
  authenticated: boolean;
  serverAuthenticated: boolean;
  provisioned: boolean;
  user: SessionUser | null;
  workspace: WorkspaceSummary | null;
  redirectTo: PostLoginPath | null;
  accessGate: AccessGateState | null;
  email: string | null;
  getStartedEnabled: boolean;
  sessionMessage: string | null;
  sessionError: string | null;
  role: E2eRole;
  setRole: (role: E2eRole) => void;
  login: () => void;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshSession: () => Promise<void>;
}

const E2eAuthContext = createContext<E2eAuthContextValue | null>(null);

export { E2eAuthContext };

export function E2eAuthProvider({ children }: { children: ReactNode }) {
  const [role, setRoleState] = useState<E2eRole>(readE2eRole);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(null);
  const [redirectTo, setRedirectTo] = useState<PostLoginPath | null>(null);
  const [accessGate, setAccessGate] = useState<AccessGateState | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [getStartedEnabled, setGetStartedEnabled] = useState(false);
  const [ready, setReady] = useState(false);

  const refreshSession = useCallback(async () => {
    try {
      const response = await apiFetch<{
        data: {
          authenticated: boolean;
          provisioned: boolean;
          getStartedEnabled?: boolean;
          email?: string | null;
          redirectTo?: PostLoginPath;
          accessGate?: AccessGateState | null;
          user?: SessionUser;
          workspace?: WorkspaceSummary | null;
        };
      }>("/api/auth/session");
      setUser(response.data.user ?? null);
      setWorkspace(response.data.workspace ?? null);
      setEmail(response.data.email ?? response.data.user?.email ?? null);
      setRedirectTo(
        response.data.redirectTo ??
          (response.data.provisioned ? POST_LOGIN_PATHS.DASHBOARD : null),
      );
      setAccessGate(response.data.accessGate ?? null);
      setGetStartedEnabled(Boolean(response.data.getStartedEnabled));
    } catch {
      setUser(null);
      setWorkspace(null);
      setRedirectTo(null);
      setAccessGate(null);
      setEmail(null);
      setGetStartedEnabled(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession, role]);

  const setRole = useCallback((next: E2eRole) => {
    if (!isE2eRole(next)) return;
    localStorage.setItem(E2E_ROLE_KEY, next);
    setRoleState(next);
  }, []);

  const value = useMemo<E2eAuthContextValue>(
    () => ({
      ready,
      authenticated: true,
      serverAuthenticated: true,
      provisioned: Boolean(user),
      user,
      workspace,
      redirectTo,
      accessGate,
      email,
      getStartedEnabled,
      sessionMessage: null,
      sessionError: null,
      role,
      setRole,
      login: () => undefined,
      logout: async () => {
        setUser(null);
        setWorkspace(null);
        setRedirectTo(null);
        setAccessGate(null);
        setEmail(null);
        setGetStartedEnabled(false);
      },
      getAccessToken: async () => "e2e-bypass-token",
      refreshSession,
    }),
    [ready, user, workspace, redirectTo, accessGate, email, getStartedEnabled, role, setRole, refreshSession],
  );

  return <E2eAuthContext.Provider value={value}>{children}</E2eAuthContext.Provider>;
}

export function useE2eAuth() {
  const ctx = useContext(E2eAuthContext);
  if (!ctx) throw new Error("useE2eAuth must be used within E2eAuthProvider");
  return ctx;
}

export function getE2eRoleHeader(): string | null {
  if (import.meta.env.VITE_E2E_AUTH_BYPASS !== "true") return null;
  return localStorage.getItem(E2E_ROLE_KEY) ?? "owner";
}
